import { useState } from 'react'

import { Button, Variants as ButtonVariants } from '@factorialco/deprecated-design-system'
import { Button as FactorialOneButton } from '@factorialco/factorial-one-react'
import { Spinner } from '@factorialco/factorial-one-react/dist/experimental'
import { Upload } from '@factorialco/factorial-one-react/icons/app'

import {
  CollectionBootstrapConfig,
  CollectionsBootstrap,
} from 'modules/core/components/CollectionsBootstrap'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'
import features from 'modules/core/store/collections/features'
import legalEntities from 'modules/core/store/collections/legalEntities'
import users from 'modules/core/store/collections/users'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employees from 'modules/employees/store/collections/employees'
import PayrollSummaryImportModal from 'modules/payroll_bundle/components/PayrollSummaryImport/PayrollSummaryImportModal/index'
import { usePayrollSummaryImportButton } from 'modules/payroll_bundle/hooks/usePayrollSummaryImportButton'
import contextlessPermissions from 'modules/permissions/store/collections/contextlessPermissions'

interface Props {
  textButton?: string
  version?: 'factorial-one' | 'factorial'
  source: string
  type?: ButtonVariants
  onOpen?: () => void // when the modal is opened
  onFinish?: () => void // when the process finish completely
  onClose?: () => void // when the modal is closed without finish the process
  contractVersions: ContractVersions
}

// This component checks the availability of using the payroll importer abstraction. The conditions are:
// - companies with at least one payroll policy created
// - Employee permission resultsSeeCompanyPayrollResults enabled
const PayrollSummaryImportButton = ({
  textButton,
  version = 'factorial-one',
  source,
  type = 'primary',
  onOpen,
  onFinish,
  onClose,
  contractVersions,
}: Props) => {
  const [modalOpened, setModalOpened] = useState(false)
  const [payrollPolicyId, setPayrollPolicyId] = useState<number | undefined>()
  const factorialOneVersion = version === 'factorial-one'
  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()

  const { nodes, loading, policy, canSeeButton } = usePayrollSummaryImportButton()

  // Collections needed for this component. This avoid to perform the load of the collections on the load
  // of the page and it is manage in the own component. Now we have to maintain this dependencies manually
  const collections: CollectionBootstrapConfig[] = [
    [companies, {}],
    [contextlessPermissions, {}],
    [employees, {}],
    [features, {}],
    [legalEntities, {}],
    [users, {}],
  ]

  useUnsafeEffect(() => {
    if (canSeeButton) setPayrollPolicyId(policy?.id)
  }, [nodes])

  const openModal = () => {
    setModalOpened(true)

    track(trackingEvents.payroll.initImportPayrollSummaryFromExternal, {
      companyId: currentCompanyId,
      source,
    })

    onOpen?.()
  }

  const onCloseModal = () => {
    setModalOpened(false)
    onClose?.()
  }

  const onFinishModal = () => {
    setModalOpened(false)

    track(trackingEvents.payroll.finishImportPayrollSummaryFromExternal, {
      companyId: currentCompanyId,
      source,
    })

    onFinish?.()
  }

  if (loading || !payrollPolicyId) return <></>

  return (
    <CollectionsBootstrap collections={collections} loadingFallback={<Spinner />}>
      {factorialOneVersion && (
        <FactorialOneButton
          onClick={() => openModal()}
          variant={'default'}
          icon={Upload}
          label={
            textButton || i18n.t('payroll_bundle.payroll_summary_import.button.default_button_text')
          }
        />
      )}

      {!factorialOneVersion && (
        <Button onClick={() => openModal()} type={type} iconName={'upload'}>
          {textButton || i18n.t('payroll_bundle.payroll_summary_import.button.default_button_text')}
        </Button>
      )}

      {modalOpened && (
        <PayrollSummaryImportModal
          companyId={currentCompanyId}
          source={source}
          onFinish={onFinishModal}
          onClose={onCloseModal}
          initialPolicyId={payrollPolicyId}
          title={i18n.t('payroll_bundle.payroll_summary_import.button.default_button_text')}
          contractVersions={contractVersions}
        />
      )}
    </CollectionsBootstrap>
  )
}

export default PayrollSummaryImportButton
